import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { CustomersModalCreate } from "./CustomersModalUpdate";
import { deleteCustomer, fetchCustomers, updateCustomerName } from "@redux/slices/tenantSlice";
import { Auth0Customer, getAuth0CustomersColumns } from "./CustomersData";

interface RawCustomer {
    id: string;
    customerId: string;
    // name: string;
    name?: string;
    Name?: string; 
    nameDisplay: string;
    domain: string;
    hasMFA: boolean;
    Products: Array < {
      id: number;
      name: string;
    }>
}

type AlertConfig = {
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: "confirm" | "success" | "error" | "warning";
    resolve: (value: boolean) => void;
};

export function CustomersPage() {
    const { tenantId } = useAppSelector(state => state.selection);
    // const tenantId = useAppSelector((state) => state.tenant.id);
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const [isFetching, setIsFetching] = useState(false);
    const [customersData, setCustomersData] = useState<Auth0Customer[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchColumn, setSearchColumn] = useState("All");
    const overlaySpinner = useProcessingOverlay(isFetching);
    const [editCustomerId, setEditCustomerId] = useState<string | null | undefined>(undefined);
    const [editedName, setEditedName] = useState<string>("");
    const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
    const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
    const dispatch = useAppDispatch();

    const showConfirmationAlert = (config: Omit<AlertConfig, "resolve">): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            setAlertConfig({ ...config, resolve });
        });
    };

    const showAlert = (config: Omit<AlertConfig, "resolve">): Promise<void> => {
        return new Promise<void>((resolve) => {
            setAlertConfig({ ...config, resolve: () => resolve() });
        });
    };

    const fetchCustomersData = async () => {
        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            const cust = await dispatch(fetchCustomers({token, tenantId})).unwrap();
            const raw = cust.data;
            const customersList = Array.isArray(raw) ? (raw as RawCustomer[]) : [];
            const processedData = customersList.map((cust: RawCustomer) => ({
                customerId: cust.customerId,
                user_id: cust.id,
                name: cust.name || cust.Name || "",
                hasMFA: cust.hasMFA,
                nameDisplay: cust.nameDisplay,
                domain: cust.domain,
                products: cust.Products
            }))
            setCustomersData(processedData);
            // console.log(cust.data, 'customers data');
        } catch (err) {
            console.error("Error fetching Auth0 users =>", err);
        } finally {
            setIsFetching(false);
        }
    }

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) return;
        if (tenantId === 0) return;
        fetchCustomersData();
    }, [isLoading, isAuthenticated, tenantId]);

    const filteredData = customersData.filter((row) => {
        const term = searchTerm.toLowerCase();
        if (!term) return true;
        const includes = (val: string) => val.toLowerCase().includes(term);
        switch (searchColumn) {
            case "Name":
                return includes(row.name);
            default:
                return (
                    includes(row.name) ||
                    row.products.some((p) => includes(p.name))
                );
        }
    });

    const handleEdit = (userId: string, currentName: string) => {
        console.log("Editing user:", userId);
        setEditedName(currentName);
        setEditCustomerId(userId);
    };
    const handleSaveEdit = async (userId: string) => {
        try {
          setIsFetching(true);
          const token = await getAccessTokenSilently();
          await dispatch(updateCustomerName({ token, userId, name: editedName })).unwrap();
          await fetchCustomersData(); // Refresh list
        } catch (err) {
          console.error("Failed to update name:", err);
        } finally {
          setEditCustomerId(undefined);
          setEditedName("");
          setIsFetching(false);
        }
      };
      
      const handleCancelEdit = () => {
        setEditCustomerId(undefined);
        setEditedName("");
      };
      
      console.log(isCreatingCustomer);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async function handleDelete(userId: string) {
        const confirmed = await showConfirmationAlert({
            title: "Are you sure?",
            description: "This action will delete the customer permanently!",
            confirmLabel: "Yes, delete it!",
            cancelLabel: "Cancel",
            variant: "warning",
        });
        if (!confirmed) return;
        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            console.log(tenantId, userId);
            dispatch(deleteCustomer({ token, tenantId, userId }))
            await showAlert({
                title: "Deleted!",
                description: "The customer has been removed.",
                confirmLabel: "OK",
                variant: "success",
            });
            await fetchCustomersData();
        } catch (err) {
            await showAlert({
                title: "Error!",
                description: "Failed to delete customer.",
                confirmLabel: "OK",
                variant: "error",
            });
        } finally {
            setIsFetching(false);
        }
    }
    const modifiedColumns = getAuth0CustomersColumns(
        handleEdit,
        handleDelete,
        editCustomerId,
        editedName,
        setEditedName,
        handleSaveEdit,
        handleCancelEdit
      );

    return (
        <>
            <div className="relative">
                <div className="p-4 bg-[var(--semantic-bg-primary)] mb-4">
                    <div className="flex items-center justify-between"></div>
                    <div className="mt-3 flex justify-end gap-3">
                        <GenericButton
                            label="Add Customer"
                            variant="brand"
                            size="md"
                            onClick={() => {
                                setIsCreatingCustomer(true);
                            }}
                        />
                    </div>
                </div>
                <div className="mb-4 bg-[var(--semantic-bg-primary)]">
                    <div className="flex items-end justify-between">
                        <div className="flex items-end space-x-4" />
                        <div className="flex items-center space-x-4">
                            <GenericFilter
                                searchColumn={searchColumn}
                                onSearchColumnChange={setSearchColumn}
                                searchTerm={searchTerm}
                                onSearchTermChange={setSearchTerm}
                                columnOptions={["All", "Name"]}
                            />
                        </div>
                    </div>
                </div>
                <GenericTable
                    columns={modifiedColumns}
                    data={filteredData}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[5, 10]}
                    width="100%"
                    maxTableBodyHeight="500px"
                    tableClassName="bg-semantic-bg-primary"
                />
            </div>
            {isCreatingCustomer && (
                <CustomersModalCreate
                    setID={() => setIsCreatingCustomer(false)}
                    fetchCustomers={fetchCustomersData}
                />
            )}
            {overlaySpinner}
            {alertConfig && (
                <GenericAlert
                    isOpen={true}
                    onConfirm={() => {
                        alertConfig.resolve(true);
                        setAlertConfig(null);
                    }}
                    onClose={() => {
                        alertConfig.resolve(false);
                        setAlertConfig(null);
                    }}
                    title={alertConfig.title}
                    description={alertConfig.description}
                    confirmLabel={alertConfig.confirmLabel}
                    cancelLabel={alertConfig.cancelLabel}
                    variant={alertConfig.variant}
                />
            )}
        </>
    );
}

export default CustomersPage;
